import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Loader2, Save, Stethoscope, Upload, FileText, Trash2,
  User, Globe, GraduationCap, Award, Clock, LogOut, CheckCircle, AlertCircle, Building2
} from 'lucide-react';
import api from '../../utils/api';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input.
import { PhoneInput } from '../../components/ui/phone-input';
import DateField from '../../components/DateField';

const SPECIALTY_OPTIONS_FALLBACK = []; // M16B-04A P0-2: master loaded from /api/public/master/specialties

const LANGUAGE_OPTIONS = [
  'English', 'Turkish', 'German', 'Arabic', 'Spanish', 'French',
  'Russian', 'Italian', 'Portuguese', 'Dutch', 'Japanese', 'Korean', 'Chinese',
];

const DOC_TYPES = [
  { value: 'diploma', label: 'Diploma' },
  { value: 'medical_license', label: 'Medical License' },
  { value: 'certification', label: 'Certification' },
];

function doctorApi() {
  return {
  };
}

export default function DoctorProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Editable form state
  const [form, setForm] = useState({});
  const [specialtyMaster, setSpecialtyMaster] = useState(SPECIALTY_OPTIONS_FALLBACK);
  const [newEdu, setNewEdu] = useState({ degree: '', institution: '', year: '' });
  const [newCert, setNewCert] = useState({ name: '', issuer: '', year: '', expiry_date: '' });
  const [uploadForm, setUploadForm] = useState({ file: null, document_type: 'diploma', document_name: '', expiry_date: '' });

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get('/doctors/me', doctorApi());
      if (data.success) {
        setProfile(data.doctor);
        setForm({
          full_name: data.doctor.full_name || '',
          phone: data.doctor.phone || '',
          bio: data.doctor.bio || '',
          specialties: data.doctor.specialties || [],
          languages: data.doctor.languages || [],
          years_of_experience: data.doctor.years_of_experience || 0,
          education: data.doctor.education || [],
          certifications: data.doctor.certifications || [],
        });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('treatx_unified_user');
        navigate('/doctor/login');
        return;
      }
      setError('Failed to load profile');
    }
  }, [navigate]);

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await api.get('/doctors/me/documents', doctorApi());
      if (data.success) setDocuments(data.documents || []);
    } catch {
      // non-blocking
    }
  }, []);

  // M16B-04A P0-2: load specialty master from SSOT instead of hard-coded list.
  const fetchSpecialtyMaster = useCallback(async () => {
    try {
      const { data } = await api.get('/public/master/specialties');
      const rows = (data?.specialties || []).filter(r => r?.is_active !== false);
      setSpecialtyMaster(rows.map(r => ({ slug: r.slug, name: r.name })));
    } catch {
      // non-blocking; component still renders with empty list and warning
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('treatx_unified_user')) { navigate('/doctor/login'); return; }

    Promise.all([fetchProfile(), fetchDocuments(), fetchSpecialtyMaster()]).finally(() => setLoading(false));
  }, [fetchProfile, fetchDocuments, fetchSpecialtyMaster, navigate]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      const { data } = await api.put('/doctors/me', form, doctorApi());
      if (data.success) {
        setProfile(data.doctor);
        setSuccessMsg('Profile saved');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleSpecialty = (s) => {
    setForm(f => ({
      ...f,
      specialties: f.specialties.includes(s)
        ? f.specialties.filter(x => x !== s)
        : [...f.specialties, s],
    }));
  };

  const toggleLanguage = (l) => {
    setForm(f => ({
      ...f,
      languages: f.languages.includes(l)
        ? f.languages.filter(x => x !== l)
        : [...f.languages, l],
    }));
  };

  const addEducation = () => {
    if (!newEdu.degree.trim() || !newEdu.institution.trim()) return;
    setForm(f => ({ ...f, education: [...f.education, { ...newEdu }] }));
    setNewEdu({ degree: '', institution: '', year: '' });
  };

  const removeEducation = (idx) => {
    setForm(f => ({ ...f, education: f.education.filter((_, i) => i !== idx) }));
  };

  const addCertification = () => {
    if (!newCert.name.trim()) return;
    setForm(f => ({ ...f, certifications: [...f.certifications, { ...newCert }] }));
    setNewCert({ name: '', issuer: '', year: '', expiry_date: '' });
  };

  const removeCertification = (idx) => {
    setForm(f => ({ ...f, certifications: f.certifications.filter((_, i) => i !== idx) }));
  };

  const handleUploadDoc = async () => {
    if (!uploadForm.file) { setError('Select a file to upload'); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', uploadForm.file);
      fd.append('document_type', uploadForm.document_type);
      fd.append('document_name', uploadForm.document_name || uploadForm.file.name);
      fd.append('expiry_date', uploadForm.expiry_date || '');

      const { data } = await api.post('/doctors/me/documents', fd, {
        ...doctorApi(),
        headers: { ...doctorApi().headers, 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setUploadForm({ file: null, document_type: 'diploma', document_name: '', expiry_date: '' });
        await fetchDocuments();
        setSuccessMsg('Document uploaded');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('treatx_unified_user');
    navigate('/doctor/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const statusBadge = {
    draft: 'bg-slate-100 text-slate-600',
    active: 'bg-emerald-100 text-emerald-700',
    pending_verification: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="doctor-profile-page">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Stethoscope className="h-5 w-5 text-teal-600" />
            <div>
              <h1 className="text-lg font-bold text-slate-900" data-testid="doctor-profile-title">Doctor Profile</h1>
              <p className="text-xs text-slate-500">Manage your professional profile and documents</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={statusBadge[profile?.status] || 'bg-slate-100'} data-testid="doctor-status-badge">
              {profile?.status || 'unknown'}
            </Badge>
            <Button variant="outline" size="sm" onClick={() => navigate('/doctor/affiliations')} data-testid="doctor-affiliations-link">
              <Building2 className="h-4 w-4 mr-1" /> Affiliations
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="doctor-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700" data-testid="doctor-error-msg">
            <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
          </div>
        )}
        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 text-sm text-emerald-700" data-testid="doctor-success-msg">
            <CheckCircle className="h-4 w-4 flex-shrink-0" /> {successMsg}
          </div>
        )}

        {/* BLOCKER 4: Prominent Status Header with Clear Next Steps */}
        <Card className={`border-2 ${
          profile?.verification_status === 'verified' ? 'border-emerald-300 bg-emerald-50/50' :
          profile?.verification_status === 'pending' ? 'border-amber-300 bg-amber-50/50' :
          'border-slate-300 bg-slate-50/50'
        }`} data-testid="doctor-status-header">
          <CardContent className="py-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  profile?.verification_status === 'verified' ? 'bg-emerald-100' :
                  profile?.verification_status === 'pending' ? 'bg-amber-100' :
                  profile?.verification_status === 'rejected' ? 'bg-red-100' : 'bg-slate-100'
                }`}>
                  {profile?.verification_status === 'verified' ? <CheckCircle className="h-6 w-6 text-emerald-600" /> :
                   profile?.verification_status === 'rejected' ? <AlertCircle className="h-6 w-6 text-red-600" /> :
                   profile?.verification_status === 'pending' ? <Clock className="h-6 w-6 text-amber-600" /> :
                   <AlertCircle className="h-6 w-6 text-slate-400" />}
                </div>
                <div>
                  <h2 className="text-lg font-bold" data-testid="verification-status-label">
                    {profile?.verification_status === 'verified' ? 'Verified' :
                     profile?.verification_status === 'pending' ? 'Pending Verification' :
                     profile?.verification_status === 'rejected' ? 'Verification Rejected' :
                     'Not Verified'}
                  </h2>
                  <p className="text-sm text-slate-600 mt-0.5" data-testid="verification-doc-count">
                    {profile?.verification_summary?.verified_mandatory || 0}/{profile?.verification_summary?.total_mandatory || 0} required documents verified
                  </p>
                  {/* Clear next step */}
                  <div className="mt-3 p-3 rounded-lg border text-sm" data-testid="doctor-next-step">
                    {profile?.verification_status === 'verified' ? (
                      <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 p-2 rounded">
                        <CheckCircle className="h-4 w-4 flex-shrink-0" />
                        <span>Your profile is verified. You can be assigned to treatments and receive patients.</span>
                      </div>
                    ) : profile?.verification_status === 'pending' ? (
                      <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-2 rounded">
                        <Clock className="h-4 w-4 flex-shrink-0" />
                        <span>Waiting for admin verification. You will be notified once your documents are reviewed.</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-slate-700 bg-slate-100 p-2 rounded">
                        <Upload className="h-4 w-4 flex-shrink-0" />
                        <span>Upload required documents below to start the verification process.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2" data-testid="eligibility-badges">
                {profile?.eligibility_flags?.is_searchable && (
                  <Badge className="bg-blue-100 text-blue-700 text-[10px]">Searchable</Badge>
                )}
                {profile?.eligibility_flags?.is_public_visible && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Public</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><User className="h-4 w-4" /> Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* SEARCH-PREMIUM-FINISH-01 (2026-06-16) — Profile photo.
                Reuses the canonical _file_storage_service pipeline that
                already backs document uploads.  No new media writer. */}
            <div className="flex items-start gap-4" data-testid="doctor-photo-section">
              <div className="flex-none w-24 h-24 rounded-full overflow-hidden border border-slate-200 bg-slate-100">
                {profile?.profile_photo_url ? (
                  <img
                    src={profile.profile_photo_url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    data-testid="doctor-photo-img"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400" data-testid="doctor-photo-placeholder">
                    <User className="h-10 w-10" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <div>
                  <Label>Profile Photo</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Square PNG, JPG or WEBP, &le; 2 MB. Visible to patients on TreatX.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    id="doctor-photo-input"
                    className="hidden"
                    data-testid="doctor-photo-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 2 * 1024 * 1024) {
                        setError('File too large (max 2MB)');
                        e.target.value = '';
                        return;
                      }
                      setUploading(true);
                      setError('');
                      try {
                        const fd = new FormData();
                        fd.append('file', f);
                        const { data } = await api.post(
                          '/doctors/me/photo',
                          fd,
                          { ...doctorApi(), headers: { ...doctorApi().headers, 'Content-Type': 'multipart/form-data' } },
                        );
                        if (data.success) {
                          setProfile(data.doctor);
                          setSuccessMsg('Profile photo updated');
                          setTimeout(() => setSuccessMsg(''), 3000);
                        }
                      } catch (err) {
                        setError(err?.response?.data?.detail || 'Photo upload failed');
                      } finally {
                        setUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('doctor-photo-input')?.click()}
                    disabled={uploading}
                    data-testid="doctor-photo-upload-btn"
                    className="gap-1"
                  >
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {profile?.profile_photo_url ? 'Change photo' : 'Upload photo'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Full Name</Label>
                <Input value={form.full_name || ''} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} data-testid="doctor-field-name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="doctor-field-phone" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input value={profile?.email || ''} disabled className="bg-slate-50" data-testid="doctor-field-email" />
            </div>
            <div>
              <Label>Years of Experience</Label>
              <Input type="number" min="0" value={form.years_of_experience || 0} onChange={e => setForm(f => ({ ...f, years_of_experience: parseInt(e.target.value) || 0 }))} data-testid="doctor-field-experience" />
            </div>
            <div>
              <Label>Bio</Label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-teal-500" value={form.bio || ''} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Professional background and expertise..." data-testid="doctor-field-bio" />
            </div>
          </CardContent>
        </Card>

        {/* Specialties */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Stethoscope className="h-4 w-4" /> Specialties</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2" data-testid="doctor-specialties">
              {specialtyMaster.length === 0 && (
                <p className="text-xs text-slate-500" data-testid="specialty-master-empty">
                  No active specialties available. Ask an administrator to add specialties at /admin/specialty-master.
                </p>
              )}
              {specialtyMaster.map(s => (
                <button key={s.slug} onClick={() => toggleSpecialty(s.slug)} className={`px-3 py-1 rounded-full text-xs border transition-all ${form.specialties?.includes(s.slug) ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'}`} data-testid={`specialty-${s.slug}`}>
                  {s.name || s.slug}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Languages */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4" /> Languages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2" data-testid="doctor-languages">
              {LANGUAGE_OPTIONS.map(l => (
                <button key={l} onClick={() => toggleLanguage(l)} className={`px-3 py-1 rounded-full text-xs border transition-all ${form.languages?.includes(l) ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'}`} data-testid={`language-${l}`}>
                  {l}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Education */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Education</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(form.education || []).map((edu, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 text-sm">
                <div className="flex-1">
                  <span className="font-medium">{edu.degree}</span> — {edu.institution} {edu.year && `(${edu.year})`}
                </div>
                <button onClick={() => removeEducation(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <div className="grid sm:grid-cols-3 gap-2">
              <Input placeholder="Degree" value={newEdu.degree} onChange={e => setNewEdu(n => ({ ...n, degree: e.target.value }))} data-testid="edu-degree" />
              <Input placeholder="Institution" value={newEdu.institution} onChange={e => setNewEdu(n => ({ ...n, institution: e.target.value }))} data-testid="edu-institution" />
              <div className="flex gap-2">
                <Input placeholder="Year" value={newEdu.year} onChange={e => setNewEdu(n => ({ ...n, year: e.target.value }))} data-testid="edu-year" />
                <Button variant="outline" size="sm" onClick={addEducation} data-testid="edu-add-btn">Add</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Certifications */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4" /> Certifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(form.certifications || []).map((cert, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 text-sm">
                <div className="flex-1">
                  <span className="font-medium">{cert.name}</span> {cert.issuer && `— ${cert.issuer}`} {cert.year && `(${cert.year})`}
                </div>
                <button onClick={() => removeCertification(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <div className="grid sm:grid-cols-3 gap-2">
              <Input placeholder="Name" value={newCert.name} onChange={e => setNewCert(n => ({ ...n, name: e.target.value }))} data-testid="cert-name" />
              <Input placeholder="Issuer" value={newCert.issuer} onChange={e => setNewCert(n => ({ ...n, issuer: e.target.value }))} data-testid="cert-issuer" />
              <div className="flex gap-2">
                <Input placeholder="Year" value={newCert.year} onChange={e => setNewCert(n => ({ ...n, year: e.target.value }))} data-testid="cert-year" />
                <Button variant="outline" size="sm" onClick={addCertification} data-testid="cert-add-btn">Add</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 h-11" data-testid="doctor-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Profile
        </Button>

        {/* Documents — BLOCKER 4: Required vs Optional markers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Documents
              {documents.length > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {documents.filter(d => d.verification_status === 'verified').length}/{documents.length} verified
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Required documents guidance */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-start gap-2" data-testid="doc-guidance">
              <FileText className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Required documents for verification:</p>
                <ul className="mt-1 space-y-0.5 text-xs text-blue-600">
                  <li className="flex items-center gap-1"><span className="text-red-500">*</span> Medical License <span className="text-blue-400">(Required)</span></li>
                  <li className="flex items-center gap-1"><span className="text-red-500">*</span> Diploma <span className="text-blue-400">(Required)</span></li>
                  <li className="flex items-center gap-1">Certification <span className="text-slate-400">(Optional)</span></li>
                </ul>
              </div>
            </div>

            {/* Existing documents */}
            {documents.length > 0 ? (
              <div className="space-y-2" data-testid="doctor-documents-list">
                {documents.map(doc => {
                  const isRequired = ['diploma', 'medical_license'].includes(doc.document_type);
                  return (
                    <div key={doc.document_id} className={`flex items-start gap-3 rounded-lg p-3 text-sm border ${
                      doc.verification_status === 'rejected' ? 'bg-red-50 border-red-200' :
                      doc.verification_status === 'verified' ? 'bg-emerald-50 border-emerald-200' :
                      'bg-slate-50 border-slate-200'
                    }`} data-testid={`doc-${doc.document_id}`}>
                      <FileText className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                        doc.verification_status === 'verified' ? 'text-emerald-500' :
                        doc.verification_status === 'rejected' ? 'text-red-400' : 'text-slate-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{doc.document_name || doc.file_name}</p>
                          {isRequired && <Badge variant="outline" className="text-[9px] border-red-300 text-red-600">Required</Badge>}
                          {!isRequired && <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500">Optional</Badge>}
                        </div>
                        <p className="text-xs text-slate-400">{doc.document_type} {doc.expiry_date && `| Expires: ${doc.expiry_date}`}</p>
                        {doc.rejection_reason && (
                          <p className="text-xs text-red-500 mt-1" data-testid={`doc-rejection-${doc.document_id}`}>Rejected: {doc.rejection_reason}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={`text-[10px] ${doc.verification_status === 'verified' ? 'border-emerald-300 text-emerald-600' : doc.verification_status === 'rejected' ? 'border-red-300 text-red-600' : 'border-amber-300 text-amber-600'}`}>
                        {doc.verification_status || 'pending'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-300" data-testid="no-documents">
                <Upload className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No documents uploaded yet</p>
                <p className="text-xs text-slate-400 mt-1">Upload your medical license and diploma to start verification</p>
              </div>
            )}

            {/* Upload form */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium text-slate-700">Upload New Document</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label>Document Type</Label>
                  <select className="w-full border rounded-md px-3 py-2 text-sm" value={uploadForm.document_type} onChange={e => setUploadForm(u => ({ ...u, document_type: e.target.value }))} data-testid="doc-type-select">
                    {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Document Name</Label>
                  <Input value={uploadForm.document_name} onChange={e => setUploadForm(u => ({ ...u, document_name: e.target.value }))} placeholder="e.g. Medical School Diploma" data-testid="doc-name-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label>File</Label>
                  <Input type="file" onChange={e => setUploadForm(u => ({ ...u, file: e.target.files?.[0] || null }))} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" data-testid="doc-file-input" />
                </div>
                <div>
                  <Label>Expiry Date (optional)</Label>
                  <DateField value={uploadForm.expiry_date} onChange={(iso) => setUploadForm(u => ({ ...u, expiry_date: iso }))} data-testid="doc-expiry-input" />
                </div>
              </div>
              <Button onClick={handleUploadDoc} disabled={uploading || !uploadForm.file} variant="outline" className="gap-2" data-testid="doc-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload Document
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}